import { Digit, Well } from './digits';
import type { DigitStyle, Theme } from './themes';

export interface ScoreboardPlayer {
  no: number | string;
  name: string;
  f: number;
  pts: number;
  /** Minutes played in m:ss format, e.g. "8:23". Empty string hides. */
  min?: string;
}

export function PlayerPanel({
  players,
  title,
  logo,
  theme,
  mode,
}: {
  players: ScoreboardPlayer[];
  title: string;
  logo: string;
  theme: Theme;
  mode: DigitStyle;
}) {
  const cols = '70px 1fr 80px 50px 80px';
  return (
    <div
      style={{
        width: '100%',
        background: theme.ac,
        borderRadius: 10,
        overflow: 'hidden',
        border: `3px solid ${theme.br}`,
        boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
          padding: '14px 16px',
          background: theme.bg,
          borderBottom: `3px solid ${theme.br}`,
        }}
      >
        <span style={{ fontSize: 36 }}>{logo}</span>
        <span
          style={{
            fontSize: 24,
            fontWeight: 900,
            color: theme.lbl,
            letterSpacing: 6,
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          {title}
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: cols,
          padding: '8px 12px',
          gap: 8,
          background: theme.bg,
          borderBottom: `2px solid ${theme.br}`,
        }}
      >
        <span style={{ ...hdr, color: theme.sub }}>NO</span>
        <span style={{ ...hdr, color: theme.sub }}>PLAYER</span>
        <span style={{ ...hdr, color: theme.sub, textAlign: 'center' }}>MIN</span>
        <span style={{ ...hdr, color: theme.sub, textAlign: 'center' }}>F</span>
        <span style={{ ...hdr, color: theme.sub, textAlign: 'center' }}>PTS</span>
      </div>
      {players.map((p, i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: cols,
            padding: '10px 12px',
            gap: 8,
            alignItems: 'center',
            background: i % 2 === 0 ? theme.ac : theme.bg,
            borderBottom: `1px solid ${theme.br}`,
            minHeight: 56,
          }}
        >
          <Well pad="4px 8px">
            <Digit val={p.no} digits={2} color="#ff1a1a" h={36} mode={mode} />
          </Well>
          <span
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: theme.lbl,
              letterSpacing: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              padding: '0 10px',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            }}
          >
            {p.name}
          </span>
          <span
            style={{
              fontSize: 22,
              fontFamily: 'ui-monospace, monospace',
              fontWeight: 800,
              color: '#9bb7d4',
              textAlign: 'center',
              letterSpacing: 0.5,
            }}
          >
            {p.min ?? '—'}
          </span>
          <Well pad="4px 6px">
            <Digit
              val={p.f}
              digits={1}
              color={p.f >= 4 ? '#fbbf24' : '#ff1a1a'}
              h={32}
              mode={mode}
            />
          </Well>
          <Well pad="4px 8px">
            <Digit val={p.pts} digits={2} color="#22c55e" h={32} mode={mode} />
          </Well>
        </div>
      ))}
    </div>
  );
}

const hdr: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 900,
  letterSpacing: 3,
};
